// IssueFilterChipBar — the search input plus four multi-select chips (Status /
// Severity / Owner / Age) above the issue register. Search matches issueRef
// AND title (the matching lives in applyIssueChipFilters); the input is
// debounced so a fast filter pipeline doesn't run per keystroke. The active
// chip uses the brand-violet active palette — one of the tab's three
// sanctioned violet slots (the others are the composer Post button and the
// drawer Update-status icon). A Clear-all control appears only when something
// is active.

import { useEffect, useRef, useState } from "react";
import {
  EMPTY_ISSUE_FILTERS,
  type IssueAgeBucket,
  type IssueChipFilters,
} from "./applyIssueChipFilters";
import type { IssueSeverity, IssueStatus } from "../../../../types";
import DebouncedSearchInput from "../shared/DebouncedSearchInput";

interface OptionDef<V extends string> {
  value: V;
  label: string;
}

const STATUS_OPTIONS: OptionDef<IssueStatus>[] = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In progress" },
  { value: "awaiting_response", label: "Awaiting response" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];

const SEVERITY_OPTIONS: OptionDef<IssueSeverity>[] = [
  { value: "critical", label: "Critical" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

const AGE_OPTIONS: OptionDef<IssueAgeBucket>[] = [
  { value: "recent", label: "≤ 7 days" },
  { value: "1-4w", label: "1–4 weeks" },
  { value: "over-30d", label: "Over 30 days" },
];

const CHIP_BASE =
  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-caption font-medium transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400";
const CHIP_INACTIVE =
  "border-slate-300 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700";
const CHIP_ACTIVE =
  "border-brand-400 bg-brand-50 text-brand-700 dark:border-brand-500 dark:bg-brand-950 dark:text-brand-300";
const POPOVER_CLASS =
  "absolute z-20 mt-1 min-w-[200px] max-w-md max-h-[60vh] overflow-y-auto rounded-md border border-slate-200 bg-white p-2 shadow-soft-lg dark:shadow-soft-lg-dark dark:border-slate-700 dark:bg-slate-800";

interface MultiSelectChipProps<V extends string> {
  chipKey: "status" | "severity" | "owner" | "age";
  label: string;
  options: OptionDef<V>[];
  selected: V[];
  onToggle: (next: V[]) => void;
}

function MultiSelectChip<V extends string>({
  chipKey,
  label,
  options,
  selected,
  onToggle,
}: MultiSelectChipProps<V>) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isActive = selected.length > 0;

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", handleKey);
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [open]);

  const handleToggleOption = (value: V) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onToggle(next);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`${CHIP_BASE} ${isActive ? CHIP_ACTIVE : CHIP_INACTIVE}`}
      >
        <span>{label}</span>
        {isActive && (
          <span className="rounded bg-white px-1 text-[10px] text-brand-600 dark:bg-brand-900 dark:text-brand-300">
            {selected.length}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid={`issue-filter-popover-${chipKey}`}
          role="listbox"
          aria-multiselectable="true"
          className={POPOVER_CLASS}
        >
          <ul className="space-y-1">
            {options.map((opt) => {
              const id = `issue-filter-${chipKey}-${opt.value}`;
              return (
                <li key={opt.value}>
                  <label
                    htmlFor={id}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-body-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700"
                  >
                    <input
                      id={id}
                      type="checkbox"
                      checked={selected.includes(opt.value)}
                      onChange={() => handleToggleOption(opt.value)}
                      className="accent-brand-600"
                    />
                    <span>{opt.label}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

export interface IssueFilterChipBarProps {
  filters: IssueChipFilters;
  onFiltersChange: (next: IssueChipFilters) => void;
  search: string;
  onSearchChange: (next: string) => void;
  /** Owner chip options — user-display names plus the Unassigned sentinel,
   *  computed by the orchestrator from the contract's issues. */
  ownerOptions: OptionDef<string>[];
}

export default function IssueFilterChipBar({
  filters,
  onFiltersChange,
  search,
  onSearchChange,
  ownerOptions,
}: IssueFilterChipBarProps) {
  // Clear-all lives on this bar (no external reset affordance, unlike the
  // milestone/deliverable bars), so the resetCounter guard is driven
  // internally: bumping it on every Clear-all cancels any in-flight search
  // debounce so a keystroke typed just before Clear-all can't re-land after
  // the debounce fires. See DebouncedSearchInput's file-top comment for the
  // full race.
  const [searchResetCounter, setSearchResetCounter] = useState(0);

  const anyActive =
    filters.status.length > 0 ||
    filters.severity.length > 0 ||
    filters.owner.length > 0 ||
    filters.age.length > 0 ||
    search.length > 0;

  const handleClearAll = () => {
    onFiltersChange(EMPTY_ISSUE_FILTERS);
    onSearchChange("");
    setSearchResetCounter((c) => c + 1);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <DebouncedSearchInput
        value={search}
        onDebouncedChange={onSearchChange}
        resetCounter={searchResetCounter}
        ariaLabel="Search issues"
        placeholder="Search issues…"
        wrapperClassName="relative min-w-[180px] max-w-[280px] flex-1"
        iconClassName="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
        inputClassName="input h-9 w-full pl-8"
      />

      <MultiSelectChip
        chipKey="status"
        label="Status"
        options={STATUS_OPTIONS}
        selected={filters.status}
        onToggle={(status) => onFiltersChange({ ...filters, status })}
      />
      <MultiSelectChip
        chipKey="severity"
        label="Severity"
        options={SEVERITY_OPTIONS}
        selected={filters.severity}
        onToggle={(severity) => onFiltersChange({ ...filters, severity })}
      />
      <MultiSelectChip
        chipKey="owner"
        label="Owner"
        options={ownerOptions}
        selected={filters.owner}
        onToggle={(owner) => onFiltersChange({ ...filters, owner })}
      />
      <MultiSelectChip
        chipKey="age"
        label="Age"
        options={AGE_OPTIONS}
        selected={filters.age}
        onToggle={(age) => onFiltersChange({ ...filters, age })}
      />

      {anyActive && (
        <button
          type="button"
          onClick={handleClearAll}
          className="ml-auto text-caption text-slate-500 underline-offset-2 hover:text-slate-700 hover:underline focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-400 dark:hover:text-slate-200"
        >
          Clear all
        </button>
      )}
    </div>
  );
}
